import React, {useEffect,useRef,useState} from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom';
import {
  selectPotreeLayer,
  setPotreeLayer,
  potreeLayerApi,
  potreeLayerContentUrl,
  isPotreeProjectFileUrl,
  setReady,
  selectReady,
} from '../app/potree.js'
import apiClient from "../app/apiClient.js";
import { loadPotreeLayer, addPotreeLayerToScene, getPotreeNodeFromLoadEvent } from '../app/potreeLib.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGlobe,
  faDove
} from '@fortawesome/free-solid-svg-icons'

import './PotreeViewer.css'
import './potree.css'

const POTREE_SIDEBAR_STYLES = [
  '/jspotree/Cloud/libs/jquery-ui/jquery-ui.min.css',
  '/jspotree/Cloud/libs/spectrum/spectrum.css',
  '/jspotree/Cloud/libs/jstree/themes/mixed/style.css',
  '/jspotree/Cloud/libs/openlayers3/ol.css',
];

function ensurePotreeSidebarStyles() {
  POTREE_SIDEBAR_STYLES.forEach((href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  });
}

console.warn = () => {};

const PotreeCanvas = React.forwardRef(({setCurrentControl, currentControl, title}, ref) => {
    return <div className="potree_container">
      {title ? <div className="potree-page-title">{title}</div> : null}
      <div id="potree_render_area" ref={ref} />
      <div id="potree_sidebar_container" />
      <div className="potree-navigation-menu">
        <div onClick={() => setCurrentControl("orbit")} className={currentControl === "orbit" ? "active" : ""}>
          <FontAwesomeIcon icon={faGlobe}/>
        </div>
        <div onClick={() => setCurrentControl("fps")} className={currentControl === "fps" ? "active" : ""}>
          <FontAwesomeIcon icon={faDove}/>
        </div>
      </div>
    </div>
});

function PotreeViewer(){
  const dispatch = useDispatch()

  const [searchParams] = useSearchParams();
  const potreeLayerId = searchParams.get("id")
  const potreeLayer = useSelector(selectPotreeLayer)
  const ready = useSelector(selectReady)
  const authToken = apiClient.getAuthToken();
  const potreeCv = useRef(null);
  const viewerReadyForId = useRef(null);
  const [currentControl,setCurrentControl] = useState("orbit")
  const [controls,setControls] = useState({})

  const scripts_to_load = [
    "/jspotree/Cloud/libs/jquery/jquery-3.1.1.min.js",
    "/jspotree/Cloud/libs/spectrum/spectrum.js",
    "/jspotree/Cloud/libs/jquery-ui/jquery-ui.min.js",
    "/jspotree/Cloud/libs/other/BinaryHeap.js",
    "/jspotree/Cloud/libs/tween/tween.min.js",
    "/jspotree/Cloud/libs/d3/d3.js",
    "/jspotree/Cloud/libs/proj4/proj4.js",
    "/jspotree/Cloud/libs/openlayers3/ol.js",
    "/jspotree/Cloud/libs/i18next/i18next.js",
    "/jspotree/Cloud/libs/jstree/jstree.js",
    "/jspotree/Cloud/build/potree/potree.js",
    "/jspotree/Cloud/libs/plasio/js/laslaz.js"
  ]

  // init potree viewer
  useEffect(() => {
    // wait for data from API
    if ((potreeLayer !== undefined) && ready) {
      // if the viewer is already ready for this layer, return
      if (viewerReadyForId.current === potreeLayer.id) return;
      viewerReadyForId.current = potreeLayer.id;

      const Potree = window.Potree;
      ensurePotreeSidebarStyles();

      window.viewer = new Potree.Viewer(potreeCv.current);

      setControls({
        fps: new Potree.FirstPersonControls(window.viewer),
        orbit: new Potree.OrbitControls(window.viewer)
      })

      const projectUrl = potreeLayer.projectUrl?.trim?.() ?? '';
      const hasProject = isPotreeProjectFileUrl(projectUrl);

      window.viewer.loadGUI(() => {
        window.viewer.setLanguage('fr');
        window.viewer.useHQ = true;
        // loadGUI appends a menu toggle each time the effect re-runs — keep one.
        [...document.querySelectorAll('#potree_quick_buttons .potree_menu_toggle')]
          .slice(1)
          .forEach((el) => el.remove());
        window.viewer.toggleSidebar();
      });

      // Potree metadata file via Cornis proxy (token + authorizer_prefix).
      const metadataProxyUrl = potreeLayerContentUrl(potreeLayer.metadataUrl, authToken, potreeLayer.id);

      loadPotreeLayer(metadataProxyUrl, potreeLayer.name, (e) => {
          const scene = window.viewer.scene;
          const potreeNode = getPotreeNodeFromLoadEvent(e);
          const material = potreeNode.material;
          material.size = 1;
          material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
          material.shape = Potree.PointShape.SQUARE;
          addPotreeLayerToScene(scene, potreeNode);
          // Project file owns the camera; fitToScreen would overwrite loadProject's view.
          if (!hasProject) window.viewer.fitToScreen();
        }
      );

      // Optional project file (camera / settings). Skip when projectUrl is "" / missing.
      if (hasProject) {
        window.viewer.loadProject(
          potreeLayerContentUrl(projectUrl, authToken, potreeLayer.id)
        );
      }
    }
  }, [ready, potreeLayer])

  useEffect(() => {
    if((potreeLayer !== undefined) && ready){
      switch(currentControl){
        case "orbit":
          window.viewer.setControls(controls.orbit)
          break;
        case "fps":
          window.viewer.setControls(controls.fps)
          break;
        default:
          window.viewer.setControls(controls.orbit)
          break;
      }
    }
  },[currentControl])

  useEffect(() => {
    if (potreeLayer === undefined){
      potreeLayerApi.get(potreeLayerId, (layer) => {
        dispatch(setPotreeLayer({potreeLayer: layer}))
      })
    }

    if (!ready){
      const key = scripts_to_load[0];
      const script = document.createElement("script");
      script.src = key;
      script.onload = () => scriptLoaded(0);
      document.body.appendChild(script);
    }
  })

  const scriptLoaded = (idx) => {
    if (idx+1 < scripts_to_load.length){
      const key = scripts_to_load[idx+1];
      const script = document.createElement("script");
      script.src = key;
      script.onload = () => scriptLoaded(idx+1);
      document.body.appendChild(script);
    }else{
      dispatch(setReady({ready: true}))
    }
  }

  return <PotreeCanvas
            ref={potreeCv}
            setCurrentControl={setCurrentControl}
            currentControl={currentControl}
            title={potreeLayer?.name?.replace(/[_-]+/g, ' ').trim()}
          />
}

export default PotreeViewer
